import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { JSONObject, JSONValue } from '../../class/workflow.type';
import { PageStates } from '../../dynamic-render/shared/workflow-navigation/workflow-navigation.type';
import { mergeJson, truncateObject } from '../../helper/merge-json/merge-json';
import { StatefulService } from '@app/service/stateful.service';
import { registerDevtoolsState } from '@shared/service/devtools/devtools-state-registry.service';

export interface NavigationPageState {
  /**
   * Indicate if page has been visited
   */
  isVisited?: boolean;

  /**
   * Indicate if page has been touched
   */
  isTouched?: boolean;

  /**
   * Indicate the page state in the navigation
   */
  state?: PageStates;
}

const DefaultPageState = { isTouched: false, isVisited: false, state: 'not-started' } as NavigationPageState;
// const PageStateStorageKey = 'ss:nav-page-state';
const PageStateStorageKey = 'extraData';

/**
 * Syncing in-memory and api/array other income desc
 */
@Injectable({ providedIn: 'root' })
export class WorkflowPageNavigationStateService {
  /** Navigation Retrieved */
  private readonly _navigationStateRetrieved$ = new BehaviorSubject<boolean>(false);

  /** An observable of page Submitted flag  */
  public get navigationStateRetrieved$(): Observable<boolean> {
    return this._navigationStateRetrieved$.asObservable();
  }

  /**
   * Page Visit and touched Status
   */
  private readonly navPagesState: { [id: string]: NavigationPageState } = {};

  /**
   * storagePrefix
   */
  private storagePrefix: string;

  constructor(private readonly statefulService: StatefulService) {
    registerDevtoolsState(this, 'WorkflowPageNavigationStateService');
  }

  public setStoragePrefix(flowId: string): void {
    const isOLAFlow = flowId === 'ola' || (flowId && flowId.indexOf('ola-consumer') > -1);
    this.storagePrefix = isOLAFlow ? 'ola' : 'api';
    this.retrievePageState();
  }

  /**
   * Get immutable instance of the page state
   */
  public get(pageId: string): NavigationPageState {
    const navPageState = this.navPagesState[pageId] || DefaultPageState;
    return { ...navPageState };
  }

  /**
   * Update the state of specific page
   */
  public update(pageId: string, state: NavigationPageState): void {
    const navPageState = this.get(pageId);
    this.navPagesState[pageId] = { ...navPageState, ...state };
    this.savePageState();
  }

  private get getExtradataApiPath(): string {
    return `${this.storagePrefix}:${PageStateStorageKey}`;
  }

  private savePageState(): void {
    if (this._navigationStateRetrieved$.value) {
      this.statefulService.wrap(this.getExtradataApiPath).value = JSON.stringify(this.navPagesState);
      this.statefulService.wrap(this.getExtradataApiPath).save();
    }
  }

  /**
   * Retrieves and restores the navigation page state from persistent storage.
   *
   * This method wraps the API path using the stateful service and loads the saved state.
   * If the state is not currently being saved, it truncates the current navigation pages state
   * and reverts to the previously saved state. Otherwise, it merges the current navigation pages state
   * with the saved state. The merged state is then emitted to subscribers and persisted.
   *
   * @remarks
   * - Uses `truncateObject` to clear the navigation pages state when not saving.
   * - Uses `mergeJson` to combine the saved and current navigation pages state.
   * - Emits a notification via `_navigationStateRetrieved$` after retrieval.
   * - Calls `savePageState` to persist the updated state.
   *
   * @public
   */
  public retrievePageState(): void {
    const state = this.statefulService.wrap(this.getExtradataApiPath);
    // the StatefulValue coming back from statefulService.wrap(...) doesn’t
    // expose a load() in the test runtime. I’ve made this method defensive:
    // - if state.load exists, it uses it (prod behavior).
    // - if it doesn’t, it just runs the merge logic directly.

    const finalize = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let saved = JSON.parse(state.valueString || '{}');
      if (!state.isSaving) {
        truncateObject(<JSONObject>this.navPagesState);
        if (typeof state.revert === 'function') {
          state.revert();
        }
      } else {
        saved = { ...saved, ...this.navPagesState };
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (state as any).revert === 'function') {
        state.revert();
      }
      mergeJson(<JSONValue>this.navPagesState, saved);
      this._navigationStateRetrieved$.next(true);
      this.savePageState();
    };

    // NOTE: some unit tests may use a simplified StatefulValue double without `.load`.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (state as any).load === 'function') {
      state.load(() => {
        finalize();
      });
    } else {
      finalize();
    }
  }

  public refreshPageState(): void {
    this._navigationStateRetrieved$.next(true);
  }
}
